import {
    Component,
    DestroyRef,
    effect,
    inject,
    Injector,
    input,
    OnInit,
    output,
    signal,
    untracked,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { FlowStateNode, FlowNodeValuePatch, NODE_PARAMS_MAP, NodeType } from '@src/app/models/flow';
import { FlowStore } from '@store/flow-store';

@Component({
    selector: 'flow-node',
    templateUrl: './flow-node.component.html',
    styleUrls: ['./flow-node.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class FlowNodeComponent implements OnInit {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _injector = inject(Injector);
    private readonly _store = inject(FlowStore);

    public readonly viewModel = input.required<FlowStateNode>();

    public readonly removeConnection = output<string>();

    protected readonly form = new FormControl<FlowNodeValuePatch>(null);
    protected readonly defaultParams = NODE_PARAMS_MAP;
    protected readonly expanded = signal(false);

    protected readonly nodeType = NodeType;

    public ngOnInit(): void {
        this._listenViewModelChanges();
        this._listenToFormChanges();
    }

    private _listenToFormChanges(): void {
        this.form.valueChanges
            .pipe(
                distinctUntilChanged((prev, curr) => this._isEqual(prev, curr)),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(value => {
                this._store.updateNode(this.viewModel().id, { value });
            });
    }

    private _isEqual<TValue>(a: TValue, b: TValue): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    private _listenViewModelChanges(): void {
        effect(
            () => {
                const viewModel = this.viewModel();
                untracked(() => {
                    this.expanded.set(viewModel.isExpanded ?? false);
                    this.form.setValue(viewModel.value, { emitEvent: false });
                });
            },
            { injector: this._injector }
        );
    }

    protected toggle(isExpanded: boolean): void {
        this._store.updateNode(this.viewModel().id, { isExpanded });
    }

    protected removeConnectionByOutput(outputId: string): void {
        this.removeConnection.emit(outputId);
    }
}
